import { AnalysisMethod } from "../ars_ldm";
import { isAnalysisProgrammingCodeTemplate } from "./isAnalysisProgrammingCodeTemplate";
import { isDocumentReference } from "./isDocumentReference";
import { isNamedObject } from "./isNamedObject";
import { isObjectRecord } from "./isObjectRecord";
import { isOperation } from "./isOperation";

/**
 * Type guard to check if a value is an AnalysisMethod.
 *
 * Validates that the object has the required properties of an AnalysisMethod:
 * - NamedObject properties (name, description?, label?)
 * - id: string
 * - operations: Operation[] (each element passes isOperation)
 * - documentRefs?: DocumentReference[]
 * - codeTemplate?: AnalysisProgrammingCodeTemplate
 *
 * @param obj - The value to check.
 * @returns True if the value is an AnalysisMethod.
 */
export const isAnalysisMethod = (obj: unknown): obj is AnalysisMethod => {
  if (!isObjectRecord(obj)) return false;
  if (!isNamedObject(obj)) return false;

  if (typeof obj.id !== "string") return false;

  // Required: operations must be an array of valid Operations
  if (!Array.isArray(obj.operations)) return false;
  if (obj.operations.some((op) => !isOperation(op))) return false;

  // Optional: documentRefs must be an array of valid DocumentReference values
  if (obj.documentRefs !== undefined) {
    if (!Array.isArray(obj.documentRefs)) return false;
    if (obj.documentRefs.some((documentReference) => !isDocumentReference(documentReference))) {
      return false;
    }
  }

  // Optional: codeTemplate must be a valid AnalysisProgrammingCodeTemplate value
  if (obj.codeTemplate !== undefined && !isAnalysisProgrammingCodeTemplate(obj.codeTemplate)) {
    return false;
  }

  return true;
};
